<template>
  <div class="layout-navbars-breadcrumb-user">
    <div class="layout-navbars-breadcrumb-user-icon" title="刷新" @click="refreshPage">
      <el-icon><RefreshRight /></el-icon>
    </div>
    <div class="layout-navbars-breadcrumb-user-icon" title="菜单搜索" @click="openSearch">
      <el-icon><Search /></el-icon>
    </div>

    <div class="layout-navbars-breadcrumb-user-icon">
      <el-dropdown trigger="click" :hide-on-click="true" placement="bottom-end" @visible-change="onNoticeVisible">
        <span class="notice-trigger">
          <el-icon><Bell /></el-icon>
          <i v-if="dealtList.length > 0" class="icon-tip" />
        </span>
        <template #dropdown>
          <el-dropdown-menu class="noticedrop">
            <el-card v-if="dealtList.length > 0" class="notice-card" shadow="never">
              <template #header>
                <span>系统通知</span>
              </template>
              <router-link
                v-for="(item, i) in dealtList"
                :key="i"
                class="item_content"
                :to="noticePath(item.path)"
             >
                <div class="title">{{ item.title }}</div>
                <div class="message">{{ item.message }}</div>
              </router-link>
              <div
               v-if="todoList.length > 3 && todoList.length !== dealtList.length"
                class="moreBtn"
                @click.stop="expandTodoList"
              >
                展开全部
                <el-icon><ArrowDown /></el-icon>
              </div>
            </el-card>
            <el-card v-else class="notice-card" shadow="never">
              <template #header>
                <span>系统通知</span>
              </template>
              <div class="tab-empty">
                <div class="empty-text">暂无系统通知</div>
              </div>
            </el-card>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>

    <div class="layout-navbars-breadcrumb-user-icon" :title="isFullscreen ? '退出全屏' : '全屏'" @click="toggleFullscreen">
      <el-icon>
        <FullScreen v-if="!isFullscreen" />
        <Crop v-else />
      </el-icon>
    </div>

    <div v-if="!userStore.isAgent" class="layout-navbars-breadcrumb-user-icon">
      <div class="platform-label">平台</div>
    </div>
    <ZoneSelector v-else v-slot="{ zoneName }">
      <div class="layout-navbars-breadcrumb-user-icon">
        <div class="platform-label" :title="zoneName">{{ zoneName || '区域' }}</div>
      </div>
    </ZoneSelector>

    <el-dropdown trigger="click" @command="onDropdownCommand">
      <span class="layout-navbars-breadcrumb-user-link">
        {{ adminLabel.adminAccount }}
        <el-icon class="el-icon--right"><ArrowDown /></el-icon>
      </span>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item command="user">个人中心</el-dropdown-item>
          <el-dropdown-item command="password" divided>修改密码</el-dropdown-item>
          <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>

    <div class="layout-navbars-breadcrumb-user-icon" title="布局配置" @click="themeStore.openSettingsDrawer()">
      <el-icon><Setting /></el-icon>
    </div>

    <header-search ref="searchRef" />
  </div>
</template>

<script setup lang="ts">
import { getCurrentInstance, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  ArrowDown,
  Bell,
  Crop,
  FullScreen,
  RefreshRight,
  Search,
  Setting,
} from '@element-plus/icons-vue';
import { useFullscreen } from '@vueuse/core';
import { ElMessage, ElMessageBox } from 'element-plus';
import { needDealtList } from '@/api/system';
import { editFormApi, passwordFormApi } from '@/api/user';
import useUserStore from '@/store/modules/user';
import useCrmebThemeConfigStore from '@/store/modules/themeConfig';
import { ensureCrmebPrefix } from '@/utils/crmebMenu';
import  {roterPre} from "@/setting";
import HeaderSearch from './HeaderSearch.vue';
import ZoneSelector from './ZoneSelector.vue';

interface TodoItem {
  path?: string;
  title?: string;
  message?: string;
}

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const themeStore = useCrmebThemeConfigStore();
const { proxy } = getCurrentInstance()!;
const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();

const searchRef = ref<InstanceType<typeof HeaderSearch> | null>(null);
const adminLabel = reactive({
  adminAccount: '',
});
const todoList = ref<TodoItem[]>([]);
const dealtList = ref<TodoItem[]>([]);

/** 从 AdminInfo 解析顶栏展示 */
function applyAdminInfo(info: Record<string, unknown> | null | undefined) {
  if (!info) return;
  adminLabel.adminAccount =
    String(info.account ?? info.realName ?? userStore.name ?? '') || '账号';
}

function readAdminFromStorage() {
  try {
    const raw = localStorage.getItem('AdminInfo');
    if (!raw) return;
    applyAdminInfo(JSON.parse(raw) as Record<string, unknown>);
  } catch {
    adminLabel.adminAccount = userStore.name || '账号';
  }
}

function loadAdminInfo() {
  readAdminFromStorage();
}

function getTodoList() {
  needDealtList()
    .then((res: any) => {
      const list = res?.data ?? res ?? [];
      todoList.value = Array.isArray(list) ? list : [];
      dealtList.value =
        todoList.value.length > 3 ? todoList.value.slice(0, 3) : todoList.value;
    })
    .catch(() => {
      todoList.value = [];
      dealtList.value = [];
    });
}

function onNoticeVisible(open: boolean) {
  if (open) getTodoList();
}

function expandTodoList() {
  dealtList.value = [...todoList.value];
}

function noticePath(path?: string) {
  if (!path) return roterPre || '/admin';
  return ensureCrmebPrefix(path);
}

function openSearch() {
  searchRef.value?.openSearch();
}

/** 刷新当前路由页面（非整页 reload，对齐 EB bus.onTagsViewRefreshRouterView） */
function refreshPage() {
  const tab = (proxy as { $tab?: { refreshPage: (obj?: object) => Promise<unknown> } }).$tab;
  if (!tab?.refreshPage) return;
  tab.refreshPage({
    name: route.name,
    path: route.path,
    query: { ...route.query },
    meta: route.meta,
  }).catch((err: unknown) => {
    console.warn('[UserBar] refreshPage', err);
  });
}

function onDropdownCommand(cmd: string) {
  const modalForm = (proxy as { $modalForm?: (fn: () => Promise<unknown>) => Promise<unknown> })
    .$modalForm;
  if (cmd === 'logout') {
    ElMessageBox.confirm('此操作将退出登录，是否继续？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    }).then(() => {
      userStore.logout().then(() => {
        router.replace('/login');
      });
    });
    return;
  }
  if (cmd === 'password' && modalForm) {
    modalForm(() => passwordFormApi());
    return;
  }
  if (cmd === 'user' && modalForm) {
    modalForm(() => editFormApi());
    return;
  }
}

onMounted(() => {
  loadAdminInfo();
  getTodoList();
});
</script>

<style scoped lang="scss">
.layout-navbars-breadcrumb-user {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
  padding-right: 15px;
  height: 50px;
  min-width: 0;

  .layout-navbars-breadcrumb-user-icon {
    padding: 0 10px;
    cursor: pointer;
    color: var(--prev-bg-topBarColor, #515a6e);
    height: 50px;
    line-height: 50px;
    display: flex;
    align-items: center;
    font-size: 18px;
    &:hover {
      background: var(--prev-color-hover, rgba(0, 0, 0, 0.04));
    }
    .notice-trigger {
      position: relative;
      display: flex;
      align-items: center;
    }
    .icon-tip {
      position: absolute;
      background: #f56464;
      width: 6px;
      height: 6px;
      border-radius: 100%;
      top: 12px;
      right: 2px;
    }
  }

  .platform-label {
    display: inline-block;
    background: var(--prev-color-primary, #4073fa);
    color: #fff;
    font-size: 12px;
    padding: 0 10px;
    height: 26px;
    line-height: 26px;
    border-radius: 10px;
    white-space: nowrap;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .layout-navbars-breadcrumb-user-link {
    cursor: pointer;
    color: var(--prev-bg-topBarColor, #515a6e);
    display: flex;
    align-items: center;
    font-size: 14px;
    padding: 0 4px;
  }
}

:deep(.noticedrop.el-dropdown-menu) {
  padding: 0;
}
.notice-card {
  width: 240px;
  border: none;
  :deep(.el-card__header) {
    padding: 10px 16px 0;
    font-weight: 600;
    border: none;
  }
  :deep(.el-card__body) {
    padding: 0 16px 12px;
  }
}
.item_content {
  display: block;
  margin-top: 10px;
  line-height: 20px;
  font-size: 13px;
  text-decoration: none;
  .title {
    color: #333;
    font-weight: 600;
  }
  .message {
    color: #666;
  }
}
.moreBtn {
  color: #666;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 8px;
  cursor: pointer;
}
.tab-empty {
  text-align: center;
  padding: 16px 0;
  .empty-text {
    color: #999;
    font-size: 12px;
  }
}
</style>
